import { env } from '@/config/env';
import { getAccessToken } from '@/lib/auth/token-store';

/**
 * API Response wrapper
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string | {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * API Error class
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * API Client configuration
 */
interface ApiClientConfig {
  baseUrl: string;
  timeout: number;
  headers?: Record<string, string>;
}

/**
 * API Client for backend communication
 */
export class ApiClient {
  private config: ApiClientConfig;
  private abortControllers: Map<string, AbortController> = new Map();

  constructor(config?: Partial<ApiClientConfig>) {
    this.config = {
      baseUrl: config?.baseUrl || env.apiUrl,
      timeout: config?.timeout || env.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    };
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    method: string,
    endpoint: string,
    options?: {
      body?: any;
      params?: Record<string, any>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    }
  ): Promise<T> {
    // Build URL - handle both absolute and relative base URLs
    let urlString: string;
    const base = this.config.baseUrl;

    if (base.startsWith('http://') || base.startsWith('https://')) {
      // Absolute base URL
      const url = new URL(endpoint, base.endsWith('/') ? base : `${base}/`);
      if (options?.params) {
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        });
      }
      urlString = url.toString();
    } else {
      // Relative base URL (e.g., /api)
      const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      urlString = `${cleanBase}${cleanEndpoint}`;
      if (options?.params) {
        const searchParams = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        const qs = searchParams.toString();
        if (qs) urlString += `?${qs}`;
      }
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    // Use provided signal or timeout signal
    const signal = options?.signal || controller.signal;

    try {
      // Auto-attach auth token if available
      // The bearer lives in the heap, never in localStorage: an XSS would read
      // storage and walk off with a usable session (lib/auth/token-store.ts).
      const authHeaders: Record<string, string> = {};
      const accessToken = getAccessToken();
      if (accessToken && !options?.headers?.Authorization) {
        authHeaders['Authorization'] = `Bearer ${accessToken}`;
      }

      // Only include Content-Type for requests with a body
      const baseHeaders = { ...this.config.headers };
      if (!options?.body) {
        delete baseHeaders['Content-Type'];
      }

      console.log(`[ApiClient] ${method} ${urlString}`);
      const response = await fetch(urlString, {
        method,
        headers: {
          ...baseHeaders,
          ...authHeaders,
          ...options?.headers,
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
        signal,
      });

      clearTimeout(timeoutId);

      console.log(`[ApiClient] Response status: ${response.status}`);

      // Handle 204 No Content (e.g. successful DELETE) — no body to parse
      if (response.status === 204) {
        return undefined as T;
      }
      
      // Parse response
      const data: ApiResponse<T> = await response.json();
      console.log(`[ApiClient] Response data:`, data);

      // Handle error responses
      if (!response.ok || !data.success) {
        // Handle both error formats:
        // - Object format: { code: string, message: string, details?: any }
        // - String format: "error message" (legacy backend responses)
        const errorInfo = data.error;
        let code = 'UNKNOWN_ERROR';
        let message = 'An unknown error occurred';
        let details: any = undefined;

        if (typeof errorInfo === 'string') {
          message = errorInfo;
        } else if (errorInfo && typeof errorInfo === 'object') {
          code = errorInfo.code || 'UNKNOWN_ERROR';
          message = errorInfo.message || 'An unknown error occurred';
          details = errorInfo.details;
        }

        throw new ApiError(code, message, response.status, details);
      }

      return data.data as T;
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError('TIMEOUT', 'Request timeout', 408);
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new ApiError('NETWORK_ERROR', 'Network error occurred', 0);
      }

      // Re-throw ApiError
      if (error instanceof ApiError) {
        throw error;
      }

      // Unknown error
      throw new ApiError(
        'UNKNOWN_ERROR',
        error instanceof Error ? error.message : 'An unknown error occurred'
      );
    }
  }

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    options?: {
      params?: Record<string, any>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    }
  ): Promise<T> {
    return this.request<T>('GET', endpoint, options);
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body?: any,
    options?: {
      params?: Record<string, any>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    }
  ): Promise<T> {
    return this.request<T>('POST', endpoint, { ...options, body });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body?: any,
    options?: {
      params?: Record<string, any>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    }
  ): Promise<T> {
    return this.request<T>('PUT', endpoint, { ...options, body });
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    body?: any,
    options?: {
      params?: Record<string, any>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    }
  ): Promise<T> {
    return this.request<T>('PATCH', endpoint, { ...options, body });
  }

  /**
   * DELETE request
   */
  async delete<T>(
    endpoint: string,
    options?: {
      params?: Record<string, any>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    }
  ): Promise<T> {
    return this.request<T>('DELETE', endpoint, options);
  }

  /**
   * Cancel all pending requests
   */
  cancelAll(): void {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{
    status: string;
    timestamp: string;
    uptime: number;
  }> {
    return this.get('/health');
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Made with Bob